<script setup>
/**
 * @description 全局错误页面 - Bold Brutalist Design
 */
import { computed } from 'vue'

const props = defineProps({
  error: {
    type: Object,
    default: () => ({}),
  },
})

const errorStatus = computed(() => {
  if (props.error.statusCode === 404) return '404'
  if (props.error.statusCode === 403) return '403'
  return '500'
})

const errorTitle = computed(() => {
  const titles = {
    404: '页面未找到',
    403: '访问被拒绝',
    500: '服务器错误',
  }
  return titles[props.error.statusCode] || '系统错误'
})

const errorSubtitle = computed(() => {
  const messages = {
    404: '您访问的页面不存在或已被移除',
    403: '您没有权限访问此页面',
    500: '服务器遇到了一些问题',
  }
  return props.error.message || messages[props.error.statusCode] || '抱歉，系统出现了一些问题'
})

const handleBackHome = () => {
  clearError({ redirect: '/', statusCode: 200 })
}

const handleReload = () => {
  window.location.reload()
}
</script>

<template>
  <div class="error-page-wrapper">
    <!-- 全局 Header -->
    <BaseHeader class="error-header" />

    <!-- 错误页面主体 -->
    <div class="error-page">
      <div class="error-container">
        <div class="error-left">
          <div class="error-code-wrapper">
            <div class="error-code">{{ errorStatus }}</div>
            <div class="error-code-overlay">{{ errorStatus }}</div>
          </div>
          <div class="error-visual">
            <div class="shape shape-1"></div>
            <div class="shape shape-2"></div>
            <div class="shape shape-3"></div>
          </div>
        </div>

        <div class="error-right">
          <div class="error-content">
            <div class="error-label">ERROR</div>
            <h1 class="error-title">{{ errorTitle }}</h1>
            <p class="error-message">{{ errorSubtitle }}</p>

            <div class="error-actions">
              <button class="action-btn primary" @click="handleBackHome">
                <span class="btn-text">返回首页</span>
                <span class="btn-arrow">→</span>
              </button>
              <button class="action-btn secondary" @click="handleReload">
                <span class="btn-icon">↻</span>
                <span class="btn-text">重新加载</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="error-background">
        <div class="grid-lines"></div>
        <div class="noise-overlay"></div>
      </div>
    </div>

    <!-- 全局 Footer -->
    <BaseFooter class="error-footer" />
  </div>
</template>

<style lang="scss" scoped>
.error-page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family:
    'Noto Sans SC',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.error-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.error-page {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  padding-top: 8rem; // 为固定 Header 留出空间
  overflow: hidden;

  @media (max-width: 768px) {
    padding-top: 6rem; // 移动端 Header 高度较小
  }
}

.error-background {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);

  .grid-lines {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(245, 63, 63, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(245, 63, 63, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
  }

  .noise-overlay {
    position: absolute;
    inset: 0;
    opacity: 0.4;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  }
}

.error-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  width: 100%;
  align-items: center;
}

.error-left {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.error-code-wrapper {
  position: relative;
  font-size: clamp(12rem, 25vw, 20rem);
  font-weight: 900;
  line-height: 0.85;
  letter-spacing: -0.05em;
  color: #f53f3f;
  user-select: none;
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.error-code {
  position: relative;
  z-index: 2;
  text-shadow: 4px 4px 0 rgba(245, 63, 63, 0.1);
}

.error-code-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, transparent 40%, #fafafa 60%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  z-index: 3;
  pointer-events: none;
}

.error-visual {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.shape {
  position: absolute;
  background: #f53f3f;
  animation: float 6s ease-in-out infinite;

  &.shape-1 {
    width: 120px;
    height: 120px;
    top: -40px;
    right: -60px;
    border-radius: 50% 50% 0 50%;
    transform: rotate(45deg);
    opacity: 0.08;
    animation-delay: 0s;
  }

  &.shape-2 {
    width: 80px;
    height: 80px;
    bottom: 20px;
    left: -40px;
    border-radius: 50%;
    opacity: 0.12;
    animation-delay: 2s;
  }

  &.shape-3 {
    width: 60px;
    height: 60px;
    top: 50%;
    right: -80px;
    transform: rotate(30deg);
    opacity: 0.06;
    animation-delay: 4s;
  }
}

.error-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: slideInRight 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
}

.error-content {
  position: relative;
  padding: 3rem 0;
}

.error-label {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #f53f3f;
  margin-bottom: 1.5rem;
  padding: 0.5rem 1rem;
  background: rgba(245, 63, 63, 0.08);
  border: 2px solid #f53f3f;
}

.error-title {
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #0a0a0a;
  margin: 0 0 1.5rem 0;
  font-family: 'Noto Sans SC', sans-serif;
}

.error-message {
  font-size: 1.125rem;
  line-height: 1.6;
  color: #666;
  margin: 0 0 3rem 0;
  max-width: 480px;
}

.error-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.action-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;

  &.primary {
    background: #f53f3f;
    color: #fafafa;
    border: 2px solid #f53f3f;

    &:hover {
      background: #cb272d;
      border-color: #cb272d;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(245, 63, 63, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.secondary {
    background: transparent;
    color: #0a0a0a;
    border: 2px solid #0a0a0a;

    &:hover {
      background: #0a0a0a;
      color: #fafafa;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(10, 10, 10, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .btn-arrow {
    font-size: 1.25rem;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .btn-icon {
    font-size: 1.125rem;
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover {
    .btn-arrow {
      transform: translateX(4px);
    }

    .btn-icon {
      transform: rotate(-180deg);
    }
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-60px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(60px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
}

@media (max-width: 900px) {
  .error-container {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }

  .error-left {
    order: -1;
  }

  .error-code-wrapper {
    font-size: clamp(8rem, 30vw, 12rem);
  }

  .error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .error-actions {
    justify-content: center;
  }

  .shape {
    display: none;
  }
}

@media (max-width: 600px) {
  .error-page {
    padding: 1.5rem;
    padding-top: 6rem;
  }

  .error-title {
    font-size: 2rem;
  }

  .error-message {
    font-size: 1rem;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1.5rem;
  }
}
</style>
